// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// cues.ts — every sound this game can make, declared once.
//
// THE REASON THIS FILE EXISTS: the engine's mixer returns SILENTLY for a cue it does not know. A
// mistyped cue is not an error, it is nothing — so a typo ships as a game with no sound and a
// completely green test suite. tests/source-hygiene.test.ts greps every play() literal in src/ and
// asserts it appears in CUES, then asserts every CUES entry has a patch some real mixer knows.
//
// Register: this is a quiet game and the mix is mostly near-silence, which is what lets the two loud
// things land. `read` is the most important sound in the game — it is a minder finding something you
// left behind, and it has to be recognisable across a room with the screen not being looked at,
// because by the time you see it happen you have already lost the tempo.

import type { Patch } from '@ben-gy/game-engine/sound';

export const CUES = [
  'step',
  'creep',
  'bolt',
  'door',
  'shut',
  'force',
  'breaker',
  'slug',
  'grab',
  'bank',
  'read',
  'spot',
  'band',
  'ratchet',
  'duct',
  'out',
  'lost',
  'select',
  'deny',
  'tick',
] as const;

export type Cue = (typeof CUES)[number];

/** Frequencies in hertz. A building at night is wood, steel and breath, so everything is low and short. */
export const PATCHES: Record<string, Patch> = {
  // moving about — pitched by what you are standing on, at the call site
  step: { type: 'triangle', freq: [190, 140], dur: 0.05, gain: 0.09 },
  creep: { type: 'sine', freq: [130, 105], dur: 0.06, gain: 0.05 },
  bolt: { type: 'square', freq: [150, 95], dur: 0.09, gain: 0.15 },
  duct: { type: 'sine', freq: [90, 150], dur: 0.22, gain: 0.11 },
  // the building's furniture
  door: { type: 'triangle', freq: [240, 320], dur: 0.09, gain: 0.11 },
  shut: { type: 'triangle', freq: [300, 180], dur: 0.08, gain: 0.11 },
  force: { type: 'sawtooth', freq: [180, 70], dur: 0.26, gain: 0.2, noise: true },
  breaker: { type: 'square', freq: [420, 120], dur: 0.16, gain: 0.13 },
  // the two loud things
  slug: { type: 'square', freq: [600, 180], dur: 0.2, gain: 0.19 },
  spot: { type: 'sawtooth', freq: [520, 780], dur: 0.3, gain: 0.22 },
  // your past catching up with you: the single most important sound in the mix
  read: { type: 'sine', freq: [330, 196], dur: 0.42, gain: 0.18 },
  // the building changing its mind about you
  band: { type: 'triangle', freq: [180, 300], dur: 0.5, gain: 0.17 },
  ratchet: { type: 'square', freq: [120, 96], dur: 0.34, gain: 0.15 },
  // money
  grab: { type: 'sine', freq: [560, 760], dur: 0.12, gain: 0.13 },
  bank: { type: 'sine', freq: [520, 900], dur: 0.24, gain: 0.16 },
  out: { type: 'sine', freq: [440, 880], dur: 0.5, gain: 0.19 },
  lost: { type: 'sine', freq: [200, 90], dur: 0.9, gain: 0.2 },
  // the interface
  select: { type: 'sine', freq: [700, 700], dur: 0.03, gain: 0.06 },
  deny: { type: 'square', freq: [170, 130], dur: 0.09, gain: 0.1 },
  tick: { type: 'sine', freq: [880, 880], dur: 0.02, gain: 0.04 },
};
